// useAppMode — single source of truth for "is this an app or a website right now?"
//
// One codebase, two experiences. Most of the difference between desktop website
// and mobile app is expressed via Tailwind's `md:` breakpoint. But some
// behaviours can't be expressed in CSS:
//
//   - Disable router transitions on desktop (feels weird with a mouse)
//   - Choose <Sheet> vs <Dialog> for modals
//   - Show install prompts only on mobile + not-yet-installed
//   - Track 'pwa' vs 'browser' source in analytics
//
// Use this hook for those cases. For pure styling, prefer Tailwind responsive
// classes — they're cheaper.

import { useEffect, useState } from 'react'

export interface AppMode {
  /** True when the app is launched from the home screen / Add to Home Screen. */
  isStandalone: boolean
  /** True for viewports under the md breakpoint (768px). */
  isMobileViewport: boolean
  /** Coarse pointer ⇒ touch device (phone, tablet, some laptops with touch). */
  isTouchDevice: boolean
  /** UA-sniff: iOS (iPhone, iPad, iPod). Used for iOS-specific install
   *  instructions since iOS Safari doesn't fire `beforeinstallprompt`. */
  isIOS: boolean
  /** UA-sniff: Android. */
  isAndroid: boolean
  /**
   * `isStandalone || isMobileViewport`. Use this as the canonical "show
   * mobile-app chrome" gate (BottomNav, AppBar, sheets, transitions).
   */
  isAppMode: boolean
}

function readMode(): AppMode {
  if (typeof window === 'undefined') {
    return {
      isStandalone: false,
      isMobileViewport: false,
      isTouchDevice: false,
      isIOS: false,
      isAndroid: false,
      isAppMode: false,
    }
  }
  const standaloneMQ = window.matchMedia?.('(display-mode: standalone)').matches ?? false
  // iOS Safari before 16.4 only exposes navigator.standalone.
  const iOSStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone === true
  const isStandalone = standaloneMQ || iOSStandalone
  const isMobileViewport = window.matchMedia?.('(max-width: 767px)').matches ?? false
  const isTouchDevice = window.matchMedia?.('(pointer: coarse)').matches ?? false
  const ua = navigator.userAgent || ''
  const isIOS = /iPad|iPhone|iPod/.test(ua) && !/MSStream/.test(ua)
  const isAndroid = /Android/.test(ua)
  return {
    isStandalone,
    isMobileViewport,
    isTouchDevice,
    isIOS,
    isAndroid,
    isAppMode: isStandalone || isMobileViewport,
  }
}

export function useAppMode(): AppMode {
  const [mode, setMode] = useState<AppMode>(readMode)

  useEffect(() => {
    const update = () => setMode(readMode())
    const standaloneMQL = window.matchMedia('(display-mode: standalone)')
    const widthMQL = window.matchMedia('(max-width: 767px)')

    standaloneMQL.addEventListener?.('change', update)
    widthMQL.addEventListener?.('change', update)
    window.addEventListener('resize', update)

    return () => {
      standaloneMQL.removeEventListener?.('change', update)
      widthMQL.removeEventListener?.('change', update)
      window.removeEventListener('resize', update)
    }
  }, [])

  return mode
}
